import Utils from '../../../utils/immersive_utils';
import { computeDailyStats } from '../compute-daily-stats';
import {
  IPortfolioPerformanceAccountReport,
  IPortfolioPerformanceHistorical,
} from '../portfolio-performance.types';

const MONTH_ORDER = [
  'JAN',
  'FEB',
  'MAR',
  'APR',
  'MAY',
  'JUN',
  'JUL',
  'AUG',
  'SEP',
  'OCT',
  'NOV',
  'DEC',
];

export interface DailyClose {
  dateStr: string; // 'YYYY-MM-DD'
  close: number;
}

const fmtPct = (value: number | null): string | null =>
  value === null ? null : `${value >= 0 ? '+' : ''}${(value * 100).toFixed(2)}%`;

const fmtRatio = (value: number | null): string | null =>
  value === null ? null : value.toFixed(2);

// Builds a report for a synthetic "buy and hold 1 unit" strategy from a
// plain daily close-price series — same shape as a real
// portfolio_performance.report, so it flows through the same Overview
// table / account-card rendering as real backend-driven accounts.
export const buildReferenceReport = (
  closes: DailyClose[],
): IPortfolioPerformanceAccountReport | null => {
  if (closes.length < 2) return null;

  const historical: IPortfolioPerformanceHistorical = {};

  for (let i = 1; i < closes.length; i++) {
    const prevClose = closes[i - 1].close;
    const { dateStr, close } = closes[i];
    const [year, monthNum, day] = dateStr.split('-');
    const monthKey = MONTH_ORDER[Number(monthNum) - 1];
    const dailyReturn = close / prevClose - 1;

    historical[year] ??= { annual_return: null, months: {} };
    historical[year].months[monthKey] ??= { monthly_return: 0, days: {} };
    historical[year].months[monthKey].days[day] = { return: dailyReturn, pv: close };
  }

  for (const yearData of Object.values(historical)) {
    let yearCompound = 1;
    for (const monthData of Object.values(yearData.months)) {
      const monthCompound = Object.values(monthData.days).reduce(
        (acc, d) => acc * (1 + d.return),
        1,
      );
      monthData.monthly_return = monthCompound - 1;
      yearCompound *= monthCompound;
    }
    yearData.annual_return = yearCompound - 1;
  }

  const stats = computeDailyStats(historical);
  const lastClose = closes[closes.length - 1].close;
  const lastReturn = closes[closes.length - 1].close / closes[closes.length - 2].close - 1;

  const now = new Date();
  const yearKey = String(now.getUTCFullYear());
  const monthKey = MONTH_ORDER[now.getUTCMonth()];
  const mtdReturn = historical[yearKey]?.months[monthKey]?.monthly_return ?? null;
  const ytdReturn = historical[yearKey]?.annual_return ?? null;

  return {
    team: 'Reference',
    strategy: 'Buy and Hold',
    description: null,
    // Buy and Hold is a synthetic 1-unit spot position, not a real backend
    // account — the Positions tab is disabled for reference reports anyway
    // (see the account card's isReference guard).
    positions: [],
    positionsTotalPv: null,
    capital: null,
    npv: Utils.valueToCurrency(lastClose, 2),
    daily: fmtPct(lastReturn),
    mtd: fmtPct(mtdReturn),
    ytd: fmtPct(ytdReturn),
    maxDD: fmtPct(stats.maxDrawdownPct),
    sharpe: fmtRatio(stats.sharpe),
    sortino: fmtRatio(stats.sortino),
    denomination: 'USD',
    historical,
    ytdReturnRaw: ytdReturn,
    dailyReturnRaw: lastReturn,
    mtdReturnRaw: mtdReturn,
    pvRaw: lastClose,
    sharpeRaw: stats.sharpe,
    sortinoRaw: stats.sortino,
    maxDDRaw: stats.maxDrawdownPct,
    // No backend batch calc behind this — it's computed client-side right
    // now, so "calculated at" is simply this moment.
    calculatedAt: new Date().toISOString(),
  };
};

// Overlays a live "today" day onto a report already built by
// buildReferenceReport, using a live intraday price (from a minute-candle
// poll — see use-reference-strategy-reports.ts). buildReferenceReport itself
// only ever sees CLOSED daily candles (today's hasn't closed yet), so without
// this, `daily`/the calendar's current-day cell would always reflect
// yesterday-vs-day-before, growing more stale the longer a session stays
// open — this mirrors how real accounts inject their own live daily_return
// (build-stats-historical.ts), just computed from a live price instead of a
// backend-provided metric.
export const injectLiveDay = (
  report: IPortfolioPerformanceAccountReport,
  livePrice: number,
): IPortfolioPerformanceAccountReport => {
  if (report.pvRaw === null || !report.historical || !Number.isFinite(livePrice) || livePrice <= 0)
    return report;

  const now = new Date();
  const yearKey = String(now.getUTCFullYear());
  const monthKey = MONTH_ORDER[now.getUTCMonth()];
  const dayKey = String(now.getUTCDate()).padStart(2, '0');

  const yearData = report.historical[yearKey] ?? { annual_return: null, months: {} };
  const monthData = yearData.months[monthKey] ?? { monthly_return: 0, days: {} };

  // Today's key should never already be present (buildReferenceReport only
  // ever sees closed days), but guard against double-counting if it somehow is.
  if (dayKey in monthData.days) return report;

  const todayReturn = livePrice / report.pvRaw - 1;

  const monthCompoundBefore = Object.values(monthData.days).reduce(
    (acc, d) => acc * (1 + d.return),
    1,
  );
  const updatedMonth = {
    ...monthData,
    days: { ...monthData.days, [dayKey]: { return: todayReturn, pv: livePrice } },
    monthly_return: monthCompoundBefore * (1 + todayReturn) - 1,
  };

  const yearCompoundBefore = Object.values(yearData.months).reduce(
    (acc, m) => acc * (1 + m.monthly_return),
    1,
  );
  const updatedAnnualReturn =
    (yearCompoundBefore / (1 + monthData.monthly_return)) * (1 + updatedMonth.monthly_return) - 1;

  return {
    ...report,
    historical: {
      ...report.historical,
      [yearKey]: {
        ...yearData,
        months: { ...yearData.months, [monthKey]: updatedMonth },
        annual_return: updatedAnnualReturn,
      },
    },
    npv: Utils.valueToCurrency(livePrice, 2),
    daily: fmtPct(todayReturn),
    mtd: fmtPct(updatedMonth.monthly_return),
    ytd: fmtPct(updatedAnnualReturn),
    dailyReturnRaw: todayReturn,
    mtdReturnRaw: updatedMonth.monthly_return,
    ytdReturnRaw: updatedAnnualReturn,
    pvRaw: livePrice,
  };
};
